import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
    listCampaigns,
    createCampaign,
    deleteCampaign,
    listProspects,
    createProspect,
    importProspectsCsv,
    PROSPECT_STATUSES,
    STATUS_COLORS,
} from "@/lib/chiroApi";
import { Plus, Trash2, Upload, FileText } from "lucide-react";
import ProspectDetail from "@/components/ProspectDetail";

export default function ChiroTracker() {
    const [campaigns, setCampaigns] = useState([]);
    const [activeCampaign, setActiveCampaign] = useState(null);
    const [prospects, setProspects] = useState([]);
    const [filterCity, setFilterCity] = useState("");
    const [filterStatus, setFilterStatus] = useState("");
    const [showCampaign, setShowCampaign] = useState(false);
    const [showProspect, setShowProspect] = useState(false);
    const [showCsv, setShowCsv] = useState(false);
    const [selected, setSelected] = useState(null);
    const [openDetail, setOpenDetail] = useState(false);

    const loadCampaigns = async () => {
        const c = await listCampaigns();
        setCampaigns(c);
        if (c.length) {
            // Pick first if nothing selected, OR refresh the active reference.
            setActiveCampaign((curr) => {
                if (!curr) return c[0];
                const fresh = c.find((x) => x.id === curr.id);
                return fresh || c[0];
            });
        } else {
            setActiveCampaign(null);
        }
    };

    const loadProspects = async () => {
        if (!activeCampaign) {
            setProspects([]);
            return;
        }
        const params = { campaign_id: activeCampaign.id };
        if (filterCity) params.city = filterCity;
        if (filterStatus) params.status = filterStatus;
        const p = await listProspects(params);
        setProspects(p);
    };

    useEffect(() => {
        loadCampaigns();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    useEffect(() => {
        loadProspects();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeCampaign?.id, filterCity, filterStatus]);

    const cities = useMemo(() => {
        const s = new Set(prospects.map((p) => p.city).filter(Boolean));
        return Array.from(s).sort();
    }, [prospects]);

    const onProspectChanged = (p) => {
        setProspects((list) => list.map((x) => (x.id === p.id ? p : x)));
        setSelected(p);
        loadCampaigns();
    };

    return (
        <div data-testid="chirotracker-page">
            <div className="mb-6">
                <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#7cc93c] mb-2">
                    // chirotracker · prospecting command center
                </div>
                <h1 className="text-4xl font-bold tracking-tight">ChiroTracker Campaigns</h1>
                <p className="font-mono text-[11px] text-[#9ca3af] mt-1">
                    Internal command center. No emails sent automatically.
                </p>
            </div>

            <div className="grid grid-cols-[260px_1fr] gap-6">
                {/* Campaign list */}
                <aside className="border border-[#262a2e] bg-[#111315]" data-testid="campaign-list">
                    <div className="flex items-center justify-between px-4 py-3 border-b border-[#262a2e]">
                        <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#9ca3af]">
                            campaigns
                        </div>
                        <button
                            onClick={() => setShowCampaign(true)}
                            data-testid="new-campaign-btn"
                            className="inline-flex items-center gap-1 font-mono text-[10px] uppercase tracking-[0.2em] border border-[#7cc93c] text-[#7cc93c] hover:bg-[#7cc93c] hover:text-black transition-colors px-2 py-1 rounded-[2px]"
                        >
                            <Plus className="w-3 h-3" /> new
                        </button>
                    </div>
                    {campaigns.length === 0 && (
                        <div className="p-6 font-mono text-[11px] text-[#9ca3af] text-center">
                            NO_CAMPAIGNS_YET
                        </div>
                    )}
                    {campaigns.map((c) => (
                        <button
                            key={c.id}
                            onClick={() => setActiveCampaign(c)}
                            data-testid={`campaign-${c.id}`}
                            className={`w-full text-left px-4 py-3 border-b last:border-b-0 border-[#262a2e] transition-colors ${
                                activeCampaign?.id === c.id
                                    ? "bg-[#1c1f22] border-l-2 border-l-[#7cc93c]"
                                    : "hover:bg-[#16191c]"
                            }`}
                        >
                            <div className="font-sans font-semibold text-[14px] truncate">{c.name}</div>
                            <div className="flex items-center justify-between mt-1">
                                <span className="font-mono text-[10px] text-[#9ca3af]">
                                    {c.city || "—"}
                                </span>
                                <span className="font-mono text-[10px] text-[#9ca3af]">
                                    {c.prospect_count} prospect{c.prospect_count === 1 ? "" : "s"}
                                </span>
                            </div>
                        </button>
                    ))}
                </aside>

                {/* Prospect table */}
                <section>
                    {!activeCampaign && (
                        <div className="border border-dashed border-[#262a2e] p-12 text-center font-mono text-[11px] text-[#9ca3af]">
                            Create a campaign to begin (e.g. Nashville, Franklin, Brentwood…).
                        </div>
                    )}
                    {activeCampaign && (
                        <>
                            <div className="flex flex-wrap items-center gap-2 mb-3">
                                <select
                                    value={filterCity}
                                    onChange={(e) => setFilterCity(e.target.value)}
                                    data-testid="filter-city"
                                    className="bg-[#111315] border border-[#262a2e] font-mono text-[11px] px-3 py-1.5 rounded-[2px]"
                                >
                                    <option value="">all cities</option>
                                    {cities.map((c) => (
                                        <option key={c} value={c}>
                                            {c}
                                        </option>
                                    ))}
                                </select>
                                <select
                                    value={filterStatus}
                                    onChange={(e) => setFilterStatus(e.target.value)}
                                    data-testid="filter-status"
                                    className="bg-[#111315] border border-[#262a2e] font-mono text-[11px] px-3 py-1.5 rounded-[2px]"
                                >
                                    <option value="">all statuses</option>
                                    {PROSPECT_STATUSES.map((s) => (
                                        <option key={s} value={s}>
                                            {s}
                                        </option>
                                    ))}
                                </select>

                                <div className="ml-auto flex items-center gap-2">
                                    <button
                                        onClick={() => setShowCsv(true)}
                                        data-testid="import-csv-btn"
                                        className="inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.2em] border border-[#262a2e] text-[#9ca3af] hover:border-[#7cc93c] hover:text-[#7cc93c] transition-colors px-3 py-1.5 rounded-[2px]"
                                    >
                                        <Upload className="w-3 h-3" /> import csv
                                    </button>
                                    <button
                                        onClick={() => setShowProspect(true)}
                                        data-testid="new-prospect-btn"
                                        className="inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.2em] border border-[#7cc93c] text-[#7cc93c] hover:bg-[#7cc93c] hover:text-black transition-colors px-3 py-1.5 rounded-[2px]"
                                    >
                                        <Plus className="w-3 h-3" /> add prospect
                                    </button>
                                    <button
                                        onClick={async () => {
                                            if (!window.confirm(`Delete campaign "${activeCampaign.name}" and all its prospects?`)) return;
                                            await deleteCampaign(activeCampaign.id);
                                            toast.success("Campaign deleted");
                                            setActiveCampaign(null);
                                            loadCampaigns();
                                        }}
                                        data-testid="delete-campaign-btn"
                                        className="inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.2em] border border-[#262a2e] text-[#9ca3af] hover:border-[#ff453a] hover:text-[#ff453a] transition-colors px-3 py-1.5 rounded-[2px]"
                                    >
                                        <Trash2 className="w-3 h-3" />
                                    </button>
                                </div>
                            </div>

                            <div className="border border-[#262a2e] bg-[#111315]" data-testid="prospect-table">
                                <div className="grid grid-cols-[1fr_120px_70px_140px_140px_140px] px-4 py-2 border-b border-[#262a2e] font-mono text-[9px] uppercase tracking-[0.25em] text-[#9ca3af]">
                                    <div>practice</div>
                                    <div>city</div>
                                    <div>zip</div>
                                    <div>email</div>
                                    <div>last contact</div>
                                    <div>status</div>
                                </div>

                                {prospects.length === 0 && (
                                    <div className="p-12 text-center font-mono text-[11px] text-[#9ca3af]">
                                        NO_PROSPECTS_YET
                                    </div>
                                )}

                                {prospects.map((p) => (
                                    <div
                                        key={p.id}
                                        onClick={() => {
                                            setSelected(p);
                                            setOpenDetail(true);
                                        }}
                                        data-testid={`prospect-row-${p.id}`}
                                        className="pc-row grid grid-cols-[1fr_120px_70px_140px_140px_140px] items-center px-4 py-3 border-b last:border-b-0 border-[#262a2e] cursor-pointer font-mono text-[11px]"
                                    >
                                        <div className="truncate text-[#f3f4f6] flex items-center gap-2">
                                            {p.draft_email && (
                                                <FileText className="w-3 h-3 text-[#7cc93c]" title="has draft" />
                                            )}
                                            {p.practice_name}
                                        </div>
                                        <div className="text-[#9ca3af] truncate">{p.city || "—"}</div>
                                        <div className="text-[#9ca3af]">{p.zip || "—"}</div>
                                        <div className="text-[#9ca3af] truncate">{p.email || "—"}</div>
                                        <div className="text-[#9ca3af]">
                                            {p.last_contacted_at?.slice(0, 10) || "—"}
                                        </div>
                                        <div>
                                            <span
                                                className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-[2px] text-[10px] uppercase tracking-[0.15em] border"
                                                style={{
                                                    color: STATUS_COLORS[p.status] || "#9ca3af",
                                                    borderColor: `${STATUS_COLORS[p.status] || "#9ca3af"}55`,
                                                    background: `${STATUS_COLORS[p.status] || "#9ca3af"}15`,
                                                }}
                                            >
                                                <span
                                                    className="w-1.5 h-1.5 rounded-full"
                                                    style={{ background: STATUS_COLORS[p.status] || "#9ca3af" }}
                                                />
                                                {p.status}
                                            </span>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </>
                    )}
                </section>
            </div>

            {/* Modals */}
            {showCampaign && (
                <NewCampaign
                    onClose={() => setShowCampaign(false)}
                    onCreated={async (c) => {
                        await loadCampaigns();
                        setActiveCampaign(c);
                    }}
                />
            )}
            {showProspect && activeCampaign && (
                <NewProspect
                    campaign={activeCampaign}
                    onClose={() => setShowProspect(false)}
                    onCreated={async () => {
                        await loadProspects();
                        await loadCampaigns();
                    }}
                />
            )}
            {showCsv && activeCampaign && (
                <ImportCsv
                    campaign={activeCampaign}
                    onClose={() => setShowCsv(false)}
                    onImported={async () => {
                        await loadProspects();
                        await loadCampaigns();
                    }}
                />
            )}

            <ProspectDetail
                prospect={selected}
                open={openDetail}
                onOpenChange={setOpenDetail}
                onChanged={onProspectChanged}
                onDeleted={async (id) => {
                    setProspects((list) => list.filter((x) => x.id !== id));
                    setOpenDetail(false);
                    await loadCampaigns();
                }}
            />
        </div>
    );
}

/* -------------------- modals -------------------- */
function ModalShell({ title, onClose, children, testid }) {
    return (
        <div
            className="fixed inset-0 z-50 bg-black/70 flex items-start justify-center p-8 overflow-y-auto"
            data-testid={testid}
            onClick={onClose}
        >
            <div
                className="w-full max-w-lg bg-[#111315] border border-[#262a2e] mt-12"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="flex items-center justify-between px-5 py-3 border-b border-[#262a2e]">
                    <div className="font-sans font-semibold text-[15px]">{title}</div>
                    <button
                        onClick={onClose}
                        className="font-mono text-[10px] text-[#9ca3af] hover:text-[#f3f4f6]"
                    >
                        close
                    </button>
                </div>
                <div className="p-5">{children}</div>
            </div>
        </div>
    );
}

function NewCampaign({ onClose, onCreated }) {
    const [form, setForm] = useState({ name: "", city: "", notes: "" });
    const [busy, setBusy] = useState(false);
    const submit = async (e) => {
        e.preventDefault();
        if (!form.name.trim()) return;
        setBusy(true);
        try {
            const c = await createCampaign(form);
            toast.success("Campaign created");
            onCreated(c);
            onClose();
        } catch {
            toast.error("Create failed");
        } finally {
            setBusy(false);
        }
    };
    return (
        <ModalShell title="New campaign" onClose={onClose} testid="new-campaign-modal">
            <form onSubmit={submit} className="space-y-3">
                <Field label="Name (required)">
                    <input
                        autoFocus
                        value={form.name}
                        onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                        placeholder="Nashville TN"
                        data-testid="campaign-name"
                        className="w-full bg-[#090a0b] border border-[#262a2e] font-mono text-[12px] px-3 py-2 focus:border-[#7cc93c] outline-none"
                    />
                </Field>
                <Field label="City">
                    <input
                        value={form.city}
                        onChange={(e) => setForm((f) => ({ ...f, city: e.target.value }))}
                        placeholder="Nashville"
                        className="w-full bg-[#090a0b] border border-[#262a2e] font-mono text-[12px] px-3 py-2 focus:border-[#7cc93c] outline-none"
                    />
                </Field>
                <Field label="Notes">
                    <textarea
                        value={form.notes}
                        onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
                        rows={3}
                        className="w-full bg-[#090a0b] border border-[#262a2e] font-mono text-[12px] px-3 py-2 focus:border-[#7cc93c] outline-none resize-none"
                    />
                </Field>
                <div className="flex justify-end pt-3 border-t border-[#262a2e]">
                    <button
                        type="submit"
                        disabled={busy}
                        data-testid="campaign-submit"
                        className="font-mono text-[11px] uppercase tracking-[0.2em] border border-[#7cc93c] text-[#7cc93c] hover:bg-[#7cc93c] hover:text-black transition-colors px-4 py-1.5 rounded-[2px]"
                    >
                        {busy ? "Creating…" : "Create"}
                    </button>
                </div>
            </form>
        </ModalShell>
    );
}

function NewProspect({ campaign, onClose, onCreated }) {
    const [form, setForm] = useState({
        practice_name: "",
        website: "",
        city: campaign.city || "",
        state: "TN",
        zip: "",
        phone: "",
        email: "",
        gbp_link: "",
        contact_name: "",
        notes: "",
    });
    const [busy, setBusy] = useState(false);
    const submit = async (e) => {
        e.preventDefault();
        if (!form.practice_name.trim()) {
            toast.error("Practice name required");
            return;
        }
        setBusy(true);
        try {
            await createProspect({ campaign_id: campaign.id, ...form });
            toast.success("Prospect added");
            onCreated();
            onClose();
        } catch {
            toast.error("Create failed");
        } finally {
            setBusy(false);
        }
    };
    return (
        <ModalShell title={`Add prospect → ${campaign.name}`} onClose={onClose} testid="new-prospect-modal">
            <form onSubmit={submit} className="space-y-3">
                <Field label="Practice name (required)">
                    <input
                        autoFocus
                        value={form.practice_name}
                        onChange={(e) => setForm((f) => ({ ...f, practice_name: e.target.value }))}
                        data-testid="prospect-name"
                        className="w-full bg-[#090a0b] border border-[#262a2e] font-mono text-[12px] px-3 py-2 focus:border-[#7cc93c] outline-none"
                    />
                </Field>
                <div className="grid grid-cols-2 gap-3">
                    <Field label="City">
                        <input
                            value={form.city}
                            onChange={(e) => setForm((f) => ({ ...f, city: e.target.value }))}
                            className="w-full bg-[#090a0b] border border-[#262a2e] font-mono text-[12px] px-3 py-2 focus:border-[#7cc93c] outline-none"
                        />
                    </Field>
                    <Field label="State">
                        <input
                            value={form.state}
                            onChange={(e) => setForm((f) => ({ ...f, state: e.target.value }))}
                            placeholder="TN"
                            data-testid="prospect-state"
                            className="w-full bg-[#090a0b] border border-[#262a2e] font-mono text-[12px] px-3 py-2 focus:border-[#7cc93c] outline-none"
                        />
                    </Field>
                </div>
                <div className="grid grid-cols-2 gap-3">
                    <Field label="ZIP">
                        <input
                            value={form.zip}
                            onChange={(e) => setForm((f) => ({ ...f, zip: e.target.value }))}
                            className="w-full bg-[#090a0b] border border-[#262a2e] font-mono text-[12px] px-3 py-2 focus:border-[#7cc93c] outline-none"
                        />
                    </Field>
                    <Field label="Phone">
                        <input
                            value={form.phone}
                            onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))}
                            className="w-full bg-[#090a0b] border border-[#262a2e] font-mono text-[12px] px-3 py-2 focus:border-[#7cc93c] outline-none"
                        />
                    </Field>
                </div>
                <Field label="Website">
                    <input
                        value={form.website}
                        onChange={(e) => setForm((f) => ({ ...f, website: e.target.value }))}
                        className="w-full bg-[#090a0b] border border-[#262a2e] font-mono text-[12px] px-3 py-2 focus:border-[#7cc93c] outline-none"
                    />
                </Field>
                <Field label="Email">
                    <input
                        value={form.email}
                        onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                        className="w-full bg-[#090a0b] border border-[#262a2e] font-mono text-[12px] px-3 py-2 focus:border-[#7cc93c] outline-none"
                    />
                </Field>
                <Field label="Google Business Profile link">
                    <input
                        value={form.gbp_link}
                        onChange={(e) => setForm((f) => ({ ...f, gbp_link: e.target.value }))}
                        className="w-full bg-[#090a0b] border border-[#262a2e] font-mono text-[12px] px-3 py-2 focus:border-[#7cc93c] outline-none"
                    />
                </Field>
                <Field label="Contact / owner name">
                    <input
                        value={form.contact_name}
                        onChange={(e) => setForm((f) => ({ ...f, contact_name: e.target.value }))}
                        className="w-full bg-[#090a0b] border border-[#262a2e] font-mono text-[12px] px-3 py-2 focus:border-[#7cc93c] outline-none"
                    />
                </Field>
                <div className="flex justify-end pt-3 border-t border-[#262a2e]">
                    <button
                        type="submit"
                        disabled={busy}
                        data-testid="prospect-submit"
                        className="font-mono text-[11px] uppercase tracking-[0.2em] border border-[#7cc93c] text-[#7cc93c] hover:bg-[#7cc93c] hover:text-black transition-colors px-4 py-1.5 rounded-[2px]"
                    >
                        {busy ? "Adding…" : "Add prospect"}
                    </button>
                </div>
            </form>
        </ModalShell>
    );
}

function ImportCsv({ campaign, onClose, onImported }) {
    const [text, setText] = useState(
        "practice_name,city,zip,phone,email,website,gbp_link,contact_name\n"
    );
    const [busy, setBusy] = useState(false);

    const submit = async () => {
        if (!text.trim().includes(",")) {
            toast.error("Need a CSV with header row");
            return;
        }
        setBusy(true);
        try {
            const r = await importProspectsCsv(campaign.id, text);
            toast.success(`Imported ${r.imported}, skipped ${r.skipped}`);
            onImported();
            onClose();
        } catch {
            toast.error("Import failed");
        } finally {
            setBusy(false);
        }
    };

    const onFile = async (e) => {
        const f = e.target.files?.[0];
        if (!f) return;
        const t = await f.text();
        setText(t);
    };

    return (
        <ModalShell title={`Import CSV → ${campaign.name}`} onClose={onClose} testid="csv-modal">
            <div className="font-mono text-[10px] text-[#9ca3af] mb-3">
                required header: <span className="text-[#f3f4f6]">practice_name</span>. optional:{" "}
                website, city, zip, phone, email, gbp_link, contact_name, status, notes.
            </div>
            <input
                type="file"
                accept=".csv,text/csv"
                onChange={onFile}
                data-testid="csv-file-input"
                className="font-mono text-[11px] text-[#9ca3af] mb-3 block"
            />
            <textarea
                value={text}
                onChange={(e) => setText(e.target.value)}
                data-testid="csv-textarea"
                rows={10}
                className="w-full bg-[#090a0b] border border-[#262a2e] font-mono text-[11px] px-3 py-2 focus:border-[#7cc93c] outline-none resize-none"
            />
            <div className="flex justify-end pt-3 mt-3 border-t border-[#262a2e]">
                <button
                    onClick={submit}
                    disabled={busy}
                    data-testid="csv-submit"
                    className="font-mono text-[11px] uppercase tracking-[0.2em] border border-[#7cc93c] text-[#7cc93c] hover:bg-[#7cc93c] hover:text-black transition-colors px-4 py-1.5 rounded-[2px]"
                >
                    {busy ? "Importing…" : "Import"}
                </button>
            </div>
        </ModalShell>
    );
}

function Field({ label, children }) {
    return (
        <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#9ca3af] mb-1.5">
                {label}
            </div>
            {children}
        </div>
    );
}
